import { Component, useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate, Outlet, Route, Routes, useLocation, useParams } from "react-router-dom";
import { Check, PencilLine, X } from "lucide-react";

import { fetchSteps, fetchStepConfig, renameProject } from "./api/client";
import logoUrl from "@/assets/svg/LOGO.svg?url";
import logoutIconUrl from "@/assets/svg/logout.svg?url";
import profileIconUrl from "@/assets/svg/profile.svg?url";
import { StepNavigator } from "./components/StepNavigator";
// import { FlagToggle } from "./components/FlagToggle";
import { ChatPanel } from "./components/ChatPanel";
import { useSessionStore } from "./hooks/useSessionStore";
import { ProjectSelectionView } from "./views/ProjectSelectionView";
import { ProjectDetailsView } from "./views/ProjectDetailsView";
import { AudioSelectionView } from "./views/AudioSelectionView";
import { TranscriptionReviewView } from "./views/TranscriptionReviewView";
import { ScenarioReviewView } from "./views/ScenarioReviewView";
import { AudioEditorView } from "./views/AudioEditorView";
import { FinalValidationView } from "./views/FinalValidationView";
import { ConfigurationScenarioView } from "./views/ConfigurationScenarioView";
import { ChoixScenarioView } from "./views/ChoixScenarioView";
import { EditionTextView } from "./views/EditionTextView";
import { AuthView } from "./views/AuthView";
import { SyntheseVocaleView } from "./views/SyntheseVocaleView";

type StepId =
  | "project_selection"
  | "project_details"
  | "audio_sources"
  | "transcription_review"
  | "scenario_review"
  | "scenario_edit"
  | "final_validation"
  | "configuration_scenario"
  | "choix_scenario"
  | "edition_text"
  | "synthese_vocale";

const STEP_COMPONENTS: Record<StepId, React.ComponentType> = {
  project_selection: ProjectSelectionView,
  project_details: ProjectDetailsView,
  audio_sources: AudioSelectionView,
  transcription_review: TranscriptionReviewView,
  scenario_review: ScenarioReviewView,
  scenario_edit: AudioEditorView,
  final_validation: FinalValidationView,
  configuration_scenario: ConfigurationScenarioView,
  choix_scenario: ChoixScenarioView,
  edition_text: EditionTextView,
  synthese_vocale: SyntheseVocaleView
};

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-3 p-8 text-center">
          <p className="text-destructive font-semibold">Une erreur inattendue s'est produite.</p>
          <p className="text-sm text-muted-foreground">{(this.state.error as Error).message}</p>
          <button
            className="text-sm text-primary underline"
            onClick={() => this.setState({ error: null })}
          >
            Réessayer
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function Layout() {
  const { setSteps, currentStep, setCurrentStep, projectName, setProjectName, logout, userEmail } = useSessionStore();
  const location = useLocation();

  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);
  const renamingRef = useRef(false);
  const cancelRef = useRef(false);
  const queryClient = useQueryClient();

  const handleStartRename = () => {
    cancelRef.current = false;
    setTempName(projectName ?? "Nouveau projet");
    setIsEditingName(true);
  };

  const handleSaveRename = async () => {
    // Enter then blur (unmount) would otherwise send a second request with the old name.
    if (renamingRef.current) return;
    const trimmed = tempName.trim();
    if (!trimmed || trimmed === projectName) {
      setIsEditingName(false);
      return;
    }
    renamingRef.current = true;
    setIsRenaming(true);
    try {
      const oldName = projectName!;
      const res = await renameProject(oldName, trimmed);
      // Drop caches keyed by the old name so nothing re-requests data/projects/<old>/.
      queryClient.removeQueries({
        predicate: (q) => typeof q.queryKey[0] === "string" && q.queryKey[1] === oldName
      });
      setProjectName(res.new_name);
      setIsEditingName(false);
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      if (res.warnings && res.warnings.length > 0) {
        alert("Projet renommé, avec avertissements :\n" + res.warnings.join("\n"));
      }
    } catch (err) {
      const e = err as { response?: { data?: { detail?: unknown } }; message?: string };
      const detail = e.response?.data?.detail;
      const message =
        typeof detail === "string" ? detail : detail !== undefined ? JSON.stringify(detail) : e.message;
      alert("Impossible de renommer le projet : " + message);
    } finally {
      renamingRef.current = false;
      setIsRenaming(false);
    }
  };

  // Sync currentStep with URL path
  useEffect(() => {
    const match = location.pathname.match(/^\/step\/([^/]+)$/);
    if (match) {
      const stepFromUrl = match[1];
      if (stepFromUrl !== currentStep) {
        setCurrentStep(stepFromUrl);
      }
    } else if (location.pathname === "/") {
      if (currentStep !== "project_selection") {
        setCurrentStep("project_selection");
      }
    }
  }, [location.pathname, currentStep, setCurrentStep]);

  const isProjectDetailsPage = location.pathname === "/step/project_details";
  const isConfigurationScenarioPage = location.pathname === "/step/configuration_scenario";
  const isChoixScenarioPage = location.pathname === "/step/choix_scenario";
  const isEditionTextPage = location.pathname === "/step/edition_text";
  const showTopProjectBanner =
    isProjectDetailsPage || isConfigurationScenarioPage || isChoixScenarioPage || isEditionTextPage;
  const [showProjectDetailsBanner, setShowProjectDetailsBanner] = useState(true);
  const [isChatCollapsed, setIsChatCollapsed] = useState(true);

  // Allow views to expand the chat panel via a CustomEvent
  useEffect(() => {
    const handler = () => setIsChatCollapsed(false);
    window.addEventListener("open-chat", handler);
    return () => window.removeEventListener("open-chat", handler);
  }, []);

  // Control chat panel collapse/open behavior based on current step
  useEffect(() => {
    if (currentStep && currentStep !== "project_selection" && currentStep !== "project_details") {
      setIsChatCollapsed(false);
    } else {
      setIsChatCollapsed(true);
    }
  }, [currentStep]);
  const { data: stepsResponse } = useQuery({ queryKey: ["steps"], queryFn: fetchSteps });

  useEffect(() => {
    if (stepsResponse?.steps) {
      setSteps(stepsResponse.steps);
    }
  }, [stepsResponse, setSteps]);

  useEffect(() => {
    if (showTopProjectBanner) {
      setShowProjectDetailsBanner(true);
    }
  }, [showTopProjectBanner]);

  const isProjectSelectionPage =
    location.pathname === "/" || location.pathname === "/step/project_selection";

  const showChat =
    isConfigurationScenarioPage ||
    isChoixScenarioPage ||
    isEditionTextPage ||
    (Boolean(currentStep) &&
      !isProjectSelectionPage &&
      currentStep !== "project_selection" &&
      currentStep !== "final_validation");

  const showStepNavigator = !isProjectSelectionPage;
  const contentTopPaddingClass = showTopProjectBanner && showProjectDetailsBanner ? "pt-[49px]" : "";

  // Derive a user-friendly name from email (e.g. abd-ellah.name@domain.com -> Abd Ellah Name)
  const userDisplayName = userEmail
    ? userEmail.split("@")[0].split(/[._-]/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")
    : "";

  return (
    <div className="flex h-screen flex-col bg-muted overflow-hidden">
      <div className="sticky top-0 z-50">
        <header className="flex items-center justify-between border-b border-border bg-background px-6 py-3 shadow-sm">
          <div className="flex min-w-0 items-center gap-3">
            <Link
              to="/"
              className="shrink-0 rounded-md outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Accueil — Mémoire des Territoires"
            >
              <img src={logoUrl} alt="" width={40} height={40} className="size-10 rounded-md" />
            </Link>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold text-foreground leading-tight">Mémoire des Territoires</h1>
              </div>
              <p className="text-xs font-normal text-muted-foreground leading-normal">Archivage et Artifacts</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {/* <FlagToggle /> */}
            {userDisplayName && (
              <span className="text-sm font-semibold text-[#0F172B]">
                {userDisplayName}
              </span>
            )}
            <button
              type="button"
              onClick={logout}
              className="inline-flex size-10 items-center justify-center rounded-md text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
              aria-label="Déconnexion"
            >
              <img src={logoutIconUrl} alt="" width={20} height={20} className="size-5" />
            </button>
          </div>
        </header>

        {showTopProjectBanner && showProjectDetailsBanner && (
          <div className="absolute left-0 right-0 top-full z-40 flex items-center justify-between gap-3 border-b border-border bg-background px-6 py-2.5">
            {isEditingName ? (
              <div className="flex h-[29px] items-center gap-3 min-w-0 flex-1 max-w-[400px]">
                <input
                  type="text"
                  value={tempName}
                  onChange={(e) => setTempName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSaveRename();
                    } else if (e.key === "Escape") {
                      cancelRef.current = true;
                      setIsEditingName(false);
                    }
                  }}
                  onBlur={() => {
                    if (cancelRef.current || renamingRef.current) return;
                    handleSaveRename();
                  }}
                  disabled={isRenaming}
                  autoFocus
                  className="h-8 w-full rounded-md border border-input bg-background px-3 py-1 text-[20px] font-semibold shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
            ) : (
              <div
                onClick={handleStartRename}
                className="flex h-[29px] items-center gap-3 min-w-0 cursor-pointer group hover:opacity-85"
                title="Cliquer pour renommer le projet"
              >
                <h2 className="truncate text-[24px] font-semibold leading-[29px] text-foreground">
                  {projectName ?? "Nouveau projet"}
                </h2>
                <PencilLine className="h-4 w-4 text-muted-foreground shrink-0 transition-colors group-hover:text-foreground" />
              </div>
            )}
            <div className="flex items-center gap-4 shrink-0">
              <div className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                <Check className="h-3.5 w-3.5 text-blue-500" />
                <span>Votre archive est enregistrée</span>
              </div>
              <button
                type="button"
                onClick={() => setShowProjectDetailsBanner(false)}
                className="inline-flex h-[33.6px] min-w-[80px] items-center justify-center gap-1 rounded-full border border-[#D0D5DD] bg-[#E2E8F0] px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-[#d6dee9]"
              >
                <X className="h-5 w-5 shrink-0  text-foreground" />
                <span>Fermer</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <div className={`flex flex-1 min-w-0 overflow-hidden ${contentTopPaddingClass}`}>
        {/* <StepNavigator /> hidden on project selection landing */}
        {showStepNavigator ? (
          <aside className="w-82 shrink-0 border-r border-border bg-background overflow-y-auto p-4">
            <StepNavigator />
          </aside>
        ) : null}

        <main className="flex-1 p-6 min-w-0 overflow-y-auto">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>

        {showChat && (
          <aside
            className={`${isChatCollapsed ? "w-[68px]" : "w-[258px]"} shrink-0 h-full min-h-0 bg-white border-l border-border overflow-hidden flex flex-col transition-[width] duration-200 ease-out`}
          >
            <ChatPanel collapsed={isChatCollapsed} onToggleCollapsed={() => setIsChatCollapsed((v) => !v)} />
          </aside>
        )}
      </div>

    </div>
  );
}

function Placeholder({ stepId }: { stepId: string }) {
  const { language, chatPlaceholder, setChatPlaceholder, steps } = useSessionStore();
  const { data } = useQuery({
    queryKey: ["step", stepId],
    queryFn: () => fetchStepConfig(stepId),
    enabled: Boolean(stepId)
  });
  const fallback = steps.find((s) => s.id === stepId);
  const localizedPlaceholder =
    data?.chat_placeholder?.[language] ?? fallback?.chat_placeholder?.[language];

  useEffect(() => {
    if (localizedPlaceholder) setChatPlaceholder(localizedPlaceholder);
  }, [localizedPlaceholder, setChatPlaceholder]);

  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-xl font-semibold text-foreground">
        {data?.name?.[language] ?? fallback?.name?.[language] ?? stepId}
      </h2>
      <p className="text-sm text-muted-foreground">{data?.description?.[language] ?? fallback?.description?.[language]}</p>
      <div className="rounded-xl border border-dashed border-border bg-muted/40 p-4">
        <p className="text-sm text-muted-foreground">{chatPlaceholder ?? localizedPlaceholder}</p>
      </div>
    </div>
  );
}

function StepRoute() {
  const { stepId } = useParams<{ stepId: string }>();
  if (!stepId) {
    const Fallback = STEP_COMPONENTS.project_selection;
    return <Fallback />;
  }
  const StepComponent = STEP_COMPONENTS[stepId as StepId];
  if (StepComponent) return <StepComponent />;
  return <Placeholder stepId={stepId} />;
}

export default function App() {
  const isAuthenticated = useSessionStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/register" element={<AuthView initialMode="register" />} />
        <Route path="/login" element={<AuthView initialMode="login" />} />
        <Route path="*" element={<Navigate to="/register" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<ProjectSelectionView />} />
        <Route path="/step/:stepId" element={<StepRoute />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
